import {
    InferredRfqColumnAndValueMappings,
    RfqColumnAndValueInferenceRequest,
} from '@lune-fe/dapi-models'
import { toTitleCase } from '@lune-fe/lune-components-lib'
import { AxiosError } from 'axios'
import { AsyncResult } from 'ts-results-es'

import { inferRfqMappings } from 'endpoints/dapi'
import {
    aircraftTypesArray,
    getMappedValue,
    ParameterName,
    PropertyMapping,
    roadFuelTypesArray,
    roadVehiclesArray,
    RowNumber,
    tradelanesArray,
    transportModeArray,
    ValueMappingSource,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import { parameterValueType, RFQTemplateState } from 'views/CalculateEmissions/RFQ/utils/types'
import {
    extractDifferentColumnValues,
    findInvalidColumnIndexes,
    inferValueMapping,
} from 'views/CalculateEmissions/RFQ/utils/utils'

import { assertType } from '../utils'

const GLOBAL_PROPERTIES = [
    {
        name: 'load' as const,
        type: 'number' as const,
    },
    {
        name: 'shippedAt' as const,
        type: 'string' as const,
    },
    {
        name: 'shipmentId' as const,
        type: 'string' as const,
    },
    {
        name: 'tradelane' as const,
        type: { enum: tradelanesArray, requiresMapping: true },
    },
]

const PER_LEG_PROPERTIES = [
    {
        name: 'transportMode' as const,
        type: { enum: [...transportModeArray], requiresMapping: true },
    },
    {
        name: 'tradelane' as const,
        type: { enum: tradelanesArray, requiresMapping: true },
    },
    {
        name: 'refrigeration' as const,
        type: { enum: 'boolean' as const, requiresMapping: true },
    },
    {
        name: 'aircraftType' as const,
        type: { enum: aircraftTypesArray, requiresMapping: true },
    },
    {
        name: 'vehicleType' as const,
        type: { enum: roadVehiclesArray, requiresMapping: true },
    },
    {
        name: 'fuel' as const,
        type: { enum: roadFuelTypesArray, requiresMapping: true },
    },
    {
        name: 'arrivalOn' as const,
        type: 'string' as const,
    },
    {
        name: 'departureOn' as const,
        type: 'string' as const,
    },
    {
        name: 'vesselImo' as const,
        type: 'string' as const,
    },
    {
        name: 'vesselName' as const,
        type: 'string' as const,
    },
    {
        name: 'flightNumber' as const,
        type: 'string' as const,
    },
]

// Per-leg location properties that need source/destination prefixes
const PER_LEG_LOCATION_PROPERTIES = [
    {
        name: 'countryCode' as const,
        type: { enum: 'country_alpha3' as const, requiresMapping: true },
    },
    {
        name: 'city' as const,
        type: 'string' as const,
    },
    {
        name: 'postcode' as const,
        type: 'string' as const,
    },
    {
        name: 'streetLine' as const,
        type: 'string' as const,
    },
    {
        name: 'lat' as const,
        type: 'number' as const,
    },
    {
        name: 'lon' as const,
        type: 'number' as const,
    },
    {
        name: 'airportCode' as const,
        type: 'airport_code' as const,
    },
    {
        name: 'portCode' as const,
        type: 'locode' as const,
    },
]

function gatherSpreadsheetData(
    workState: RFQTemplateState,
): { name: string; values: Set<string> }[] {
    // Reuse already extracted data from workState instead of recalculating
    const headerRowNumber = RowNumber.fromOneBased(workState.headerRow)
    const endRow = RowNumber.fromZeroBased(workState.endRow)

    // Use the same column extraction logic as manual inference
    const spreadsheetColumns = workState.headers.map((header, columnIndex) => {
        const columnValues = extractDifferentColumnValues(
            workState.sheet,
            workState.headerStartIndex + columnIndex,
            headerRowNumber.nextRow(),
            endRow,
        )

        return {
            name: header,
            values: new Set(columnValues),
        }
    })

    return spreadsheetColumns
}

function generateCandidateIndexes(
    exampleRow: string[],
    headers: string[],
    propertyName: ParameterName,
): number[] {
    const valueType = parameterValueType[propertyName]
    const invalidColumnIndexes = findInvalidColumnIndexes(headers, exampleRow, valueType)
    // For logging purposes, lets get valid header names. Indexes aren't useful.
    const candidateColumnHeaders: string[] = []
    const candidates = headers
        .map((header, index) => ({ header, index }))
        .filter(({ header, index }) => {
            const validHeader = !invalidColumnIndexes.includes(index)
            if (validHeader) {
                candidateColumnHeaders.push(header)
            }
            return validHeader
        })
        .map((item) => item.index)
    console.log(`For ${propertyName} property: ${candidateColumnHeaders}`)
    return candidates
}

function buildRfqInferenceRequest(
    spreadsheetColumns: { name: string; values: Set<string> }[],
    exampleRow: string[],
    headers: string[],
    numberOfLegs: number,
): RfqColumnAndValueInferenceRequest {
    const globalProperties = GLOBAL_PROPERTIES.map((p) => ({
        name: p.name,
        type: p.type,
        candidateColumns: generateCandidateIndexes(exampleRow, headers, p.name),
    }))

    // Regular per-leg properties (no source/destination variants needed)
    const regularPerLegProperties = PER_LEG_PROPERTIES.map((p) => ({
        name: p.name,
        type: p.type,
        candidateColumns: generateCandidateIndexes(exampleRow, headers, p.name),
    }))

    // Location properties with source/destination prefixes
    const locationProperties = PER_LEG_LOCATION_PROPERTIES.flatMap((p) => {
        const candidateColumns = generateCandidateIndexes(exampleRow, headers, p.name)
        return [
            {
                name: `source${toTitleCase(p.name)}` as const,
                type: p.type,
                candidateColumns,
            },
            {
                name: `destination${toTitleCase(p.name)}` as const,
                type: p.type,
                candidateColumns,
            },
        ]
    })

    const perLegProperties = [...regularPerLegProperties, ...locationProperties]

    const request = {
        spreadsheetColumns: spreadsheetColumns.map((col) => ({
            name: col.name,
            values: Array.from(col.values),
        })),
        globalProperties,
        perLegProperties,
        numberOfShippingLegs: numberOfLegs,
    }

    // Pretty-print the generated request with column headers instead of indexes
    // All this logging is temporary, will be removed after we gain confidence the code works.
    console.log('Generated RFQ Inference Request:')
    console.log('Number of shipping legs:', numberOfLegs)
    console.log(
        'Spreadsheet columns:',
        spreadsheetColumns.map((col) => col.name),
    )

    console.log('Global properties:')
    globalProperties.forEach((prop) => {
        const columnHeaders = prop.candidateColumns.map((index) => spreadsheetColumns[index].name)
        console.log(`  ${prop.name}: ${JSON.stringify(prop.type)} -> [${columnHeaders.join(', ')}]`)
    })

    console.log('Per-leg properties:')
    perLegProperties.forEach((prop) => {
        const columnHeaders = prop.candidateColumns.map((index) => spreadsheetColumns[index].name)
        console.log(`  ${prop.name}: ${JSON.stringify(prop.type)} -> [${columnHeaders.join(', ')}]`)
    })

    return request
}

function useLLMColumnMapping(
    workState: RFQTemplateState,
): AsyncResult<InferredRfqColumnAndValueMappings, AxiosError> {
    const spreadsheetColumns = gatherSpreadsheetData(workState)
    const request = buildRfqInferenceRequest(
        spreadsheetColumns,
        workState.exampleRow,
        workState.headers,
        workState.numberOfLegs,
    )
    return inferRfqMappings(request)
}

/**
 * Makes sure that an extracted LLM mapping is valid. For value mappings we need to make sure we present all
 * the values to the user regardless of what the LLM returned. Make sure every input is being mapped, falling
 * back to manual inference if any of the value mappings was not returned in the LLM mapping.
 */
export function repairLLMPropertyMapping<T>(
    llmMapping: PropertyMapping<T>,
    mappingType: ValueMappingSource,
    outputValues: readonly T[],
    parameter: ParameterName,
    columnValuesExtractor: (columnIndex: number) => string[],
): PropertyMapping<T> {
    if (
        mappingType === 'mapping' &&
        llmMapping.source === 'column' &&
        llmMapping.valueMapping.source === 'mapping' // Guaranteed with previous logic, used to narrow types
    ) {
        const valuesToMap = columnValuesExtractor(llmMapping.columnIndex)
        const mappings = llmMapping.valueMapping.mappings
        const validMappings = mappings.filter(([i, _v]: [string, T]) => valuesToMap.includes(i))
        const missingMappings = valuesToMap.filter((v: string) =>
            getMappedValue(mappings, v).isNone(),
        )
        const newMappings = inferValueMapping<T>(missingMappings, outputValues, parameter)
        return {
            ...llmMapping,
            valueMapping: {
                ...llmMapping.valueMapping,
                mappings: validMappings.concat(newMappings),
            },
        }
    }

    return llmMapping
}
/**
 * Extract a specific property mapping from LLM-based inference results, handling both
 * value mapping and unit mapping.
 *
 * @param llmResults - The inference results returned by {@link useLLMColumnMapping}.
 * @param headers - Array of spreadsheet column headers
 * @param propertyName - Name of the property to extract (e.g., 'load', 'transportMode', 'countryCode')
 * @param legNumber - Which shipping leg this property belongs to (undefined for global properties like 'load')
 * @param outputValues - Valid values for this property type (e.g., loadUnitsArray, transportModeArray)
 *
 * @returns PropertyMapping configuration or undefined if no suitable mapping found
 */
export function extractLLMPropertyMapping<T>(
    llmResults: InferredRfqColumnAndValueMappings,
    headers: string[],
    propertyName: string,
    legNumber: number | undefined,
    outputValues: readonly T[],
    mappingType: ValueMappingSource,
): PropertyMapping<T> | undefined {
    let mapping = llmResults.columnMapping.find(
        (m) => m.destinationProperty === propertyName && m.legNumber === legNumber,
    )

    if (!mapping && legNumber) {
        /* We allow the LLM to infer certain properties at leg AND global scope to give us something to fallback to. For
        example, if the LLM doesn't infer a `tradelane` column for leg 3 but infers a global `tradelane` value then we
        can use this as a fallback. */
        mapping = llmResults.columnMapping.find(
            (m) => m.destinationProperty === propertyName && m.legNumber === undefined,
        )
    }

    if (!mapping) {
        console.log(`LLM ${propertyName} leg ${legNumber} -> undefined because no mapping`)
        return undefined
    }

    const columnIndex = headers.indexOf(mapping.spreadsheetColumn)
    if (columnIndex === -1) {
        console.log(`LLM ${propertyName} leg ${legNumber} -> undefined because bad column`)
        return undefined
    }

    const baseMapping = {
        source: 'column' as const,
        columnIndex,
    }

    // Handle properties with value mappings from LLM
    if (mappingType === 'mapping') {
        const mappings: [string, NonNullable<T>][] =
            mapping.valueMapping && mapping.valueMapping.length > 0
                ? mapping.valueMapping
                      .map((valueMapping) => {
                          const destinationValue = outputValues.find(
                              (v) => String(v) === valueMapping.destinationValue,
                          )
                          return destinationValue
                              ? ([valueMapping.spreadsheetValue, destinationValue] as const)
                              : null
                      })
                      .filter(
                          (valueMapping): valueMapping is [string, NonNullable<T>] =>
                              valueMapping !== null,
                      )
                : []

        console.log(
            `LLM ${propertyName} leg ${legNumber} -> ${headers[columnIndex]}, ` +
                (mappings.length > 0 ? JSON.stringify(mappings) : 'no mappings'),
        )
        return {
            ...baseMapping,
            valueMapping: {
                source: 'mapping',
                mappings,
            },
        }
    }

    // Handle unit mapping - use LLM loadUnit if available for load property
    if (mappingType === 'unit') {
        console.log(
            `LLM ${propertyName} leg ${legNumber} -> ${headers[columnIndex]}, ${llmResults.loadUnit}`,
        )
        return {
            ...baseMapping,
            // Fallback to `t` if no unit inferred by LLM
            valueMapping: {
                source: 'unit',
                unit:
                    propertyName === 'load' && llmResults.loadUnit
                        ? (llmResults.loadUnit as T)
                        : ('t' as T),
            },
        }
    }

    assertType<'direct'>(mappingType)
    console.log(`LLM ${propertyName} leg ${legNumber} -> ${headers[columnIndex]}, direct`)
    return {
        ...baseMapping,
        valueMapping: {
            source: 'direct',
        },
    }
}

export { useLLMColumnMapping, PER_LEG_LOCATION_PROPERTIES }
